import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface LogEntry {
  timestamp: string;
  level: 'info' | 'success' | 'warning' | 'error';
  message: string;
  data?: any;
}

export default function ReferralDebugPage() {
  const { user, session } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const addLog = (level: LogEntry['level'], message: string, data?: any) => {
    const log: LogEntry = {
      timestamp: new Date().toLocaleTimeString(),
      level,
      message,
      data
    };
    setLogs(prev => [...prev, log]);
  };

  const testReferralApplication = async () => {
    setIsRunning(true);
    setLogs([]);

    try {
      addLog('info', '=== 开始测试 REFERRAL 系统 (使用数据库函数) ===');

      // Step 1: Check user
      if (!user || !session) {
        addLog('error', '用户未登录');
        setIsRunning(false);
        return;
      }

      addLog('success', `用户已登录: ${user.id}`);
      addLog('info', '用户 metadata:', user.user_metadata);

      // Step 2: Get referral code from metadata
      const metadata = user.user_metadata;
      const referralCodeToApply = metadata?.referral_code_to_apply;

      if (!referralCodeToApply) {
        addLog('warning', 'metadata 中没有 referral_code_to_apply');
        setIsRunning(false);
        return;
      }

      addLog('success', `找到 referral code: ${referralCodeToApply}`);

      // Step 3: Check user type
      const userType = metadata?.user_type || 'client';
      addLog('info', `用户类型: ${userType}`);

      // Step 4: 直接调用数据库函数（跳过前端 RLS 检查）
      addLog('info', '调用数据库函数 apply_referral_code...');

      const { data: functionResult, error: functionError } = await supabase.rpc('apply_referral_code', {
        p_referral_code: referralCodeToApply,
        p_user_type: userType
      });

      addLog('info', '数据库函数返回:', { functionResult, functionError });

      if (functionError) {
        addLog('error', '数据库函数调用失败！', functionError);
        addLog('error', `错误代码: ${functionError.code}`);
        addLog('error', `错误消息: ${functionError.message}`);
      } else {
        // Type assertion for the RPC result
        const result = functionResult as { success?: boolean; error?: string } | null;
        if (result?.success) {
          addLog('success', '✅ Referral 应用成功（通过数据库函数）！', result);
        } else {
          addLog('error', '数据库函数返回失败:', result?.error);
        }
      }

    } catch (err) {
      addLog('error', '发生异常:', err);
    } finally {
      addLog('info', '=== 测试结束 ===');
      setIsRunning(false);
    }
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'success': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getLevelIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      default: return '📝';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Referral 系统调试工具</h1>

        <Card className="p-6 mb-6">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold mb-2">用户信息</h2>
              {user ? (
                <div className="space-y-1 text-sm">
                  <p><strong>User ID:</strong> {user.id}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>User Type:</strong> {user.user_metadata?.user_type || 'N/A'}</p>
                  <p><strong>Referral Code to Apply:</strong> {user.user_metadata?.referral_code_to_apply || 'N/A'}</p>
                </div>
              ) : (
                <p className="text-red-600">未登录</p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={testReferralApplication}
                disabled={isRunning || !user}
                size="lg"
              >
                {isRunning ? '测试中...' : '🔍 测试 Referral 应用'}
              </Button>
              <Button
                onClick={clearLogs}
                variant="outline"
                size="lg"
              >
                清空日志
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">调试日志</h2>
          <div className="space-y-2 font-mono text-sm max-h-[600px] overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-400">点击上方按钮开始测试...</p>
            ) : (
              logs.map((log, index) => (
                <div key={index} className="border-b pb-2">
                  <div className="flex items-start gap-2">
                    <span>{getLevelIcon(log.level)}</span>
                    <span className="text-gray-400">{log.timestamp}</span>
                    <span className={getLevelColor(log.level)}>{log.message}</span>
                  </div>
                  {log.data && (
                    <pre className="mt-1 ml-8 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
